import { useState, useEffect } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { BarChart3, TrendingUp, Eye, Heart, Bookmark, MessageCircle, RefreshCw, Loader2 } from "lucide-react";
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell, Legend
} from "recharts";

const CHART_COLORS = ["#c6a96f", "#5ba89d", "#c47272", "#5b8ec4"];

function MetricCard({ label, value, icon: Icon, color = "text-zinc-100" }) {
  return (
    <div className="bg-studio-raised border border-[#2a2e36] rounded-xl p-5 brand-card">
      <div className="flex items-center justify-between mb-2">
        <span className="section-label">{label}</span>
        <Icon size={16} className="text-zinc-500" />
      </div>
      <p className={`stat-number text-2xl font-semibold ${color}`}>{value}</p>
    </div>
  );
}

function BrandAnalyticsCard({ summary }) {
  const { brand } = summary;
  return (
    <div className="bg-studio-raised border border-[#2a2e36] rounded-xl p-5 brand-card" data-testid={`analytics-brand-${brand.id}`}>
      <div className="flex items-center gap-3 mb-4">
        <div className="w-3 h-3 rounded-full" style={{ background: brand.color_hex }} />
        <h3 className="text-sm font-semibold text-zinc-200">{brand.name}</h3>
      </div>
      <div className="grid grid-cols-2 gap-4">
        <div>
          <span className="text-xs text-zinc-500">Posts</span>
          <p className="stat-number text-lg text-zinc-100">{summary.post_count}</p>
        </div>
        <div>
          <span className="text-xs text-zinc-500">Total Reach</span>
          <p className="stat-number text-lg text-zinc-100">{summary.total_reach.toLocaleString()}</p>
        </div>
        <div>
          <span className="text-xs text-zinc-500">Avg Engagement</span>
          <p className="stat-number text-lg" style={{ color: brand.color_hex }}>{summary.avg_engagement}</p>
        </div>
        <div>
          <span className="text-xs text-zinc-500">Total Saves</span>
          <p className="stat-number text-lg text-teal">{summary.total_saves}</p>
        </div>
      </div>
    </div>
  );
}

function TopPostCard({ post, rank }) {
  return (
    <div className="flex items-center gap-4 py-3 border-b border-[#222630] last:border-0">
      <span className="stat-number text-sm text-zinc-500 w-6">{rank}</span>
      <div className="w-12 h-12 rounded-lg overflow-hidden bg-studio-surface flex-shrink-0">
        {post.thumbnail_url ? (
          <img src={post.thumbnail_url} alt="" className="w-full h-full object-cover" />
        ) : (
          <div className="w-full h-full flex items-center justify-center"><BarChart3 size={16} className="text-zinc-600" /></div>
        )}
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-sm text-zinc-200 truncate">{post.video_title || "Untitled"}</p>
        <div className="flex items-center gap-1 mt-0.5">
          <span className="w-2 h-2 rounded-full" style={{ background: post.brand?.color_hex || "#71717a" }} />
          <span className="text-xs text-zinc-500">{post.brand?.name}</span>
        </div>
      </div>
      <div className="flex items-center gap-4 text-xs">
        <span className="flex items-center gap-1 text-zinc-400"><Eye size={12} /><span className="font-mono">{(post.reach || 0).toLocaleString()}</span></span>
        <span className="flex items-center gap-1 text-zinc-400"><Heart size={12} /><span className="font-mono">{post.likes || 0}</span></span>
        <span className="flex items-center gap-1 text-teal"><Bookmark size={12} /><span className="font-mono">{post.saves || 0}</span></span>
      </div>
    </div>
  );
}

const fmtWatch = (ms) => (ms > 0 ? `${(ms / 1000).toFixed(1)}s` : "—");
const fmtNum = (n) => (n || n === 0 ? Number(n).toLocaleString() : "—");

function PerformanceTable({ posts, onCapture, capturing }) {
  return (
    <div className="bg-studio-raised border border-[#2a2e36] rounded-xl p-6">
      <div className="flex items-center justify-between mb-1">
        <h3 className="text-sm font-semibold text-zinc-200">Post Performance</h3>
        <button onClick={onCapture} disabled={capturing}
          className="text-[11px] px-2.5 py-1 rounded-full border border-gold/30 text-gold hover:bg-gold/5 transition-colors flex items-center gap-1 disabled:opacity-40"
          data-testid="capture-insights-btn">
          {capturing ? <Loader2 size={10} className="animate-spin" /> : <RefreshCw size={10} />}
          Capture now
        </button>
      </div>
      <p className="section-label mb-4">// per-post metrics · captured 1, 3 and 7 days after publish</p>
      {posts.length === 0 ? (
        <p className="text-zinc-500 text-sm text-center py-6">
          No captured metrics yet — they appear as published posts hit the 1-day mark.
        </p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead>
              <tr className="text-left text-[10px] font-mono uppercase tracking-wider text-zinc-600 border-b border-[#222630]">
                <th className="py-2 pr-3 font-normal">post</th>
                <th className="py-2 px-2 font-normal">edit</th>
                <th className="py-2 px-2 font-normal text-right">views</th>
                <th className="py-2 px-2 font-normal text-right">reach</th>
                <th className="py-2 px-2 font-normal text-right">likes</th>
                <th className="py-2 px-2 font-normal text-right">comments</th>
                <th className="py-2 px-2 font-normal text-right">saves</th>
                <th className="py-2 px-2 font-normal text-right">shares</th>
                <th className="py-2 pl-2 font-normal text-right">avg watch</th>
              </tr>
            </thead>
            <tbody>
              {posts.map((p) => (
                <tr key={p.id} className="border-b border-[#1c2026] hover:bg-studio-surface/50">
                  <td className="py-2.5 pr-3">
                    <div className="flex items-center gap-2 min-w-[180px]">
                      <span className="w-1.5 h-1.5 rounded-full flex-shrink-0" style={{ background: p.brand?.color_hex || "#71717a" }} />
                      <div className="min-w-0">
                        <p className="text-zinc-300 truncate max-w-[220px]">{p.video_title || "Untitled"}</p>
                        <p className="text-[10px] text-zinc-600 font-mono">
                          {p.published_at ? new Date(p.published_at).toLocaleDateString() : ""}
                          {p.caption_angle ? ` · ${p.caption_angle}` : ""}
                        </p>
                      </div>
                    </div>
                  </td>
                  <td className="py-2.5 px-2 text-[10px] font-mono text-zinc-500 whitespace-nowrap">
                    {p.clip_duration ? `${Math.round(p.clip_duration)}s` : "—"}
                    {p.clip_hook ? " · hook" : ""}
                    {p.clip_subtitle_count > 0 ? " · subs" : ""}
                  </td>
                  <td className="py-2.5 px-2 text-right font-mono text-zinc-300">{fmtNum(p.views)}</td>
                  <td className="py-2.5 px-2 text-right font-mono text-zinc-400">{fmtNum(p.reach)}</td>
                  <td className="py-2.5 px-2 text-right font-mono text-zinc-400">{fmtNum(p.likes)}</td>
                  <td className="py-2.5 px-2 text-right font-mono text-zinc-400">{fmtNum(p.comments)}</td>
                  <td className="py-2.5 px-2 text-right font-mono text-teal">{fmtNum(p.saves)}</td>
                  <td className="py-2.5 px-2 text-right font-mono text-zinc-400">{fmtNum(p.shares)}</td>
                  <td className="py-2.5 pl-2 text-right font-mono text-zinc-400">{fmtWatch(p.avg_watch_ms)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

export default function Analytics() {
  const [data, setData] = useState(null);
  const [postRows, setPostRows] = useState([]);
  const [capturing, setCapturing] = useState(false);
  const [loading, setLoading] = useState(true);

  const fetchAll = async () => {
    try {
      const [sumRes, postsRes] = await Promise.all([
        api.get("/analytics/summary"),
        api.get("/analytics/posts", { params: { days: 90 } }),
      ]);
      setData(sumRes.data);
      setPostRows(postsRes.data.posts || []);
    } catch { toast.error("Failed to load analytics"); }
    setLoading(false);
  };

  useEffect(() => { fetchAll(); }, []);

  const handleCapture = async () => {
    setCapturing(true);
    try {
      const res = await api.post("/analytics/capture-now?force=1");
      toast.success(`Captured ${res.data.captured} post(s)`);
      await fetchAll();
    } catch { toast.error("Capture failed"); }
    setCapturing(false);
  };

  if (loading) {
    return <div className="flex items-center justify-center h-40"><div className="spinner-gold" /></div>;
  }

  const summaries = data?.summaries || [];
  const topPosts = data?.top_posts || [];

  const totalReach = summaries.reduce((a, s) => a + s.total_reach, 0);
  const totalLikes = summaries.reduce((a, s) => a + s.total_likes, 0);
  const totalSaves = summaries.reduce((a, s) => a + s.total_saves, 0);
  const totalPosts = summaries.reduce((a, s) => a + s.post_count, 0);

  const barData = summaries.map((s) => ({
    name: s.brand.short_name || s.brand.name.slice(0, 6),
    reach: s.total_reach,
    likes: s.total_likes,
    saves: s.total_saves,
    color: s.brand.color_hex,
  }));

  const pieData = summaries.map((s, i) => ({
    name: s.brand.short_name || s.brand.name,
    value: s.post_count,
    color: CHART_COLORS[i % CHART_COLORS.length],
  }));

  const tooltipStyle = {
    contentStyle: { background: "#1a1d21", border: "1px solid #2a2e36", borderRadius: "12px", fontSize: "12px", color: "#d4d4d8" },
    cursor: { fill: "rgba(198,169,111,0.05)" },
  };

  return (
    <div className="space-y-8 animate-fadeIn" data-testid="analytics-dashboard">
      <div>
        <h1 className="font-display text-3xl font-bold text-zinc-100">Analytics</h1>
        <p className="section-label mt-2">// performance metrics</p>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 stagger-children">
        <MetricCard label="// total reach" value={totalReach.toLocaleString()} icon={Eye} />
        <MetricCard label="// total likes" value={totalLikes.toLocaleString()} icon={Heart} />
        <MetricCard label="// total saves" value={totalSaves.toLocaleString()} icon={Bookmark} color="text-teal" />
        <MetricCard label="// total posts" value={totalPosts} icon={TrendingUp} />
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        {summaries.map((s) => (
          <BrandAnalyticsCard key={s.brand.id} summary={s} />
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-studio-raised border border-[#2a2e36] rounded-xl p-6">
          <h3 className="text-sm font-semibold text-zinc-200 mb-1">Reach by Brand</h3>
          <p className="section-label mb-4">// total reach comparison</p>
          <ResponsiveContainer width="100%" height={250}>
            <BarChart data={barData}>
              <XAxis dataKey="name" tick={{ fill: "#71717a", fontSize: 11, fontFamily: "Azeret Mono" }} axisLine={{ stroke: "#222630" }} tickLine={false} />
              <YAxis tick={{ fill: "#71717a", fontSize: 11, fontFamily: "Azeret Mono" }} axisLine={false} tickLine={false} />
              <Tooltip {...tooltipStyle} />
              <Bar dataKey="reach" radius={[6, 6, 0, 0]}>
                {barData.map((entry, i) => (
                  <Cell key={i} fill={entry.color} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>

        <div className="bg-studio-raised border border-[#2a2e36] rounded-xl p-6">
          <h3 className="text-sm font-semibold text-zinc-200 mb-1">Posts by Brand</h3>
          <p className="section-label mb-4">// distribution</p>
          <ResponsiveContainer width="100%" height={250}>
            <PieChart>
              <Pie data={pieData} cx="50%" cy="50%" innerRadius={60} outerRadius={90} dataKey="value" stroke="none">
                {pieData.map((entry, i) => (
                  <Cell key={i} fill={entry.color} />
                ))}
              </Pie>
              <Tooltip {...tooltipStyle} />
              <Legend
                formatter={(value) => <span style={{ color: "#a1a1aa", fontSize: "12px", fontFamily: "DM Sans" }}>{value}</span>}
              />
            </PieChart>
          </ResponsiveContainer>
        </div>
      </div>

      <PerformanceTable posts={postRows} onCapture={handleCapture} capturing={capturing} />

      <div className="bg-studio-raised border border-[#2a2e36] rounded-xl p-6">
        <h3 className="text-sm font-semibold text-zinc-200 mb-1">Top Posts</h3>
        <p className="section-label mb-4">// by reach</p>
        {topPosts.length === 0 ? (
          <p className="text-zinc-500 text-sm text-center py-6">No published posts yet</p>
        ) : (
          topPosts.map((post, i) => <TopPostCard key={post.id} post={post} rank={i + 1} />)
        )}
      </div>
    </div>
  );
}
